import { useMemo, useState } from 'react'
import { motion } from 'motion/react'
import { Sparkles } from 'lucide-react'
import { DIETAS, DIETAS_INFO, generarSemana, huecosLibres, type DietaId } from '@shared/dietas'
import { MEAL_SLOT_LABELS } from '@shared/events'
import { recipesById } from '@shared/plate'
import type { DayKey, WeekKey } from '@shared/time'
import { ponerPropuestas } from '../lib/acciones'
import { useEvents } from '../lib/hooks'
import { useMostrarExtras } from '../lib/preferencias'
import { useSession } from '../lib/session'
import { useAviso } from './Aviso'
import { Hoja } from './Hoja'
import { Boton } from './ui'

/**
 * Elegir una dieta y rellenar los huecos de la semana con ella.
 *
 * Sólo rellena: lo que ya está puesto se queda. Lo que sale son platos
 * normales del plan, así que la semana generada se edita como cualquier otra.
 */
export function GenerarSemana({
  weekKey,
  desde,
  onCerrar,
}: {
  weekKey: WeekKey
  desde: DayKey
  onCerrar: () => void
}) {
  const events = useEvents()
  const { user } = useSession()
  const avisar = useAviso()
  const { franjas } = useMostrarExtras()
  const [dieta, setDieta] = useState<DietaId>('mediterranea')
  const [generando, setGenerando] = useState(false)

  const huecos = useMemo(
    () => huecosLibres({ weekKey, events, franjas, desde }),
    [weekKey, events, franjas, desde],
  )
  const recetas = useMemo(() => recipesById(events).size, [events])

  async function generar(): Promise<void> {
    if (!user) return
    setGenerando(true)
    const propuestas = generarSemana({ weekKey, events, dieta, franjas, desde, semilla: Date.now() })
    const deshacer = await ponerPropuestas(propuestas, user.id)
    onCerrar()
    avisar(
      propuestas.length === 0
        ? 'No había recetas que encajaran en los huecos.'
        : `${propuestas.length} platos puestos con la dieta ${DIETAS_INFO[dieta].label.toLowerCase()}`,
      propuestas.length > 0 ? { label: 'Deshacer', onClick: () => void deshacer() } : undefined,
    )
  }

  const nombresFranjas = franjas.map((slot) => MEAL_SLOT_LABELS[slot].toLowerCase()).join(', ')

  return (
    <Hoja
      titulo="Generar la semana"
      descripcion={`Rellena los huecos de ${nombresFranjas} desde hoy. Lo que ya está puesto no se toca, y todo lo que salga se puede cambiar después.`}
      onCerrar={onCerrar}
    >
      <div role="radiogroup" aria-label="Dieta" className="grid gap-2">
        {DIETAS.map((id) => {
          const info = DIETAS_INFO[id]
          const activa = dieta === id
          return (
            <motion.button
              key={id}
              type="button"
              role="radio"
              aria-checked={activa}
              whileTap={{ scale: 0.985 }}
              onClick={() => setDieta(id)}
              className={`rounded-2xl border px-4 py-3 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
                activa ? 'border-primary/60 bg-accent' : 'border-border/70 bg-card hover:border-primary/40'
              }`}
            >
              <span className={`block font-display text-lg ${activa ? 'text-primary' : ''}`}>{info.label}</span>
              <span className="mt-0.5 block text-sm text-muted-foreground">{info.descripcion}</span>
            </motion.button>
          )
        })}
      </div>

      {recetas === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          No hay recetas en el recetario todavía, así que no hay de dónde tirar.
        </p>
      ) : (
        <p className="mt-4 text-sm text-muted-foreground">
          {huecos === 0
            ? 'No queda ningún hueco libre en esta semana.'
            : `${huecos} ${huecos === 1 ? 'hueco libre' : 'huecos libres'}, a elegir entre ${recetas} recetas.`}
        </p>
      )}

      <Boton
        variante="principal"
        ancho
        className="mt-3"
        disabled={generando || huecos === 0 || recetas === 0}
        onClick={() => void generar()}
      >
        <Sparkles className="h-4 w-4" />
        Generar
      </Boton>
    </Hoja>
  )
}
